import { motion } from "framer-motion";
import { Video, MonitorPlay, Server, MessagesSquare, FileText, Mic2, BadgeIndianRupee } from "lucide-react";
import { SectionHead, staggerContainer, staggerItem } from "../Reveal";
import { IMAGES } from "../../data/site";

const PILLARS = [
  {
    icon: Video,
    title: "1-to-1 Live Interactive Classes",
    desc: "No 500-student webinars. Every session is private — your pace, your doubts, your custom syllabus.",
    span: "lg:col-span-5",
  },
  {
    icon: MonitorPlay,
    title: "Live Class Recordings",
    desc: "Every live session recorded in HD with timestamped lab walkthroughs. Revise anytime.",
    span: "lg:col-span-3",
  },
  {
    icon: Server,
    title: "24x7 Dedicated Lab Pods",
    desc: "Real Cisco, Palo Alto, FortiGate, F5, EVE-NG, AWS & Azure topologies — practice even at 2 AM.",
    span: "lg:col-span-4",
    image: true,
  },
  {
    icon: MessagesSquare,
    title: "1-on-1 Mentor Desk",
    desc: "Direct private channel to your trainer. Stuck on a lab at midnight? Ping, get unblocked.",
    span: "lg:col-span-4",
  },
  {
    icon: FileText,
    title: "Notes PDF & Lab Workbooks",
    desc: "Exhaustive cheat sheets and step-by-step workbooks written by L3 engineers — not copy-paste dumps.",
    span: "lg:col-span-4",
  },
  {
    icon: Mic2,
    title: "Real Mock Interviews",
    desc: "Grill sessions with actual MNC interview rubrics — tougher than the real thing, on purpose.",
    span: "lg:col-span-4",
  },
];

export const About = () => (
  <section id="one-to-one" data-testid="about-section" className="relative py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHead
        eyebrow="Why LearnWithTAC"
        title={
          <>
            One Student. One Industry Expert.{" "}
            <span className="text-gradient-orange">One Career Transformed.</span>
          </>
        }
        sub="For the first time in the market — true one-to-one training with working industry expert engineers, at pricing built for students, not corporations."
      />

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-5"
      >
        {PILLARS.map(({ icon: Icon, title, desc, span, image }) => (
          <motion.div
            key={title}
            variants={staggerItem}
            data-testid={`pillar-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
            className={`${span} group relative overflow-hidden rounded-2xl border border-sky-500/20 bg-[#0B1226] p-7 hover:border-sky-400/50 hover:-translate-y-1.5 hover:shadow-[0_0_30px_rgba(56,189,248,0.15)] transition-all duration-300`}
          >
            {image && (
              <>
                <img src={IMAGES.datacenter} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15 group-hover:opacity-25 transition-opacity duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1226] via-[#0B1226]/80 to-transparent" />
              </>
            )}
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-blue-600/20 border border-sky-500/30 flex items-center justify-center mb-5 group-hover:border-orange-500/50 group-hover:bg-orange-500/10 transition-colors duration-300">
                <Icon className="w-6 h-6 text-sky-400 group-hover:text-orange-400 transition-colors duration-300" />
              </div>
              <h3 className="font-display text-lg sm:text-xl font-semibold text-white">{title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{desc}</p>
            </div>
          </motion.div>
        ))}

        <motion.div
          variants={staggerItem}
          className="sm:col-span-2 lg:col-span-12 relative overflow-hidden rounded-2xl border border-orange-500/40 bg-gradient-to-r from-orange-500/15 via-[#0B1226] to-blue-700/20 p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          data-testid="affordable-pricing-strip"
        >
          <div className="w-14 h-14 shrink-0 rounded-2xl bg-orange-500/20 border border-orange-500/50 flex items-center justify-center">
            <BadgeIndianRupee className="w-7 h-7 text-orange-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-xl sm:text-2xl font-bold text-white">
              Industry-Expert Training, <span className="text-gradient-orange">Student-Friendly Pricing</span>
            </h3>
            <p className="mt-1.5 text-sm sm:text-base text-slate-300">
              Big institutes charge lakhs for recorded batches. We deliver private 1-on-1 mentorship with working MNC
              engineers for a fraction of that — because one trainer, one student, zero middlemen.
            </p>
          </div>
          <a
            data-testid="pricing-whatsapp-btn"
            href="#contact"
            className="shrink-0 inline-flex items-center rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-display font-semibold text-sm px-6 py-3.5 transition-colors duration-200"
          >
            Get Course Pricing
          </a>
        </motion.div>
      </motion.div>
    </div>
  </section>
);
